import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, CalendarClock, Lock } from "lucide-react";
import { SITE, waLink } from "@/lib/site";

const LINKS = [
  { label: "Parco Auto", href: "#catalogo" },
  { label: "Servizi", href: "#servizi" },
  { label: "Chi Siamo", href: "#chi-siamo" },
  { label: "Recensioni", href: "#recensioni" },
  { label: "Contatti", href: "#contatti" },
];

export const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNavigate(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass border-b border-border" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[84px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center group"
        >
          <img
            src="/logo.png"
            alt="Manno Auto S.r.l."
            className="h-12 sm:h-14 w-auto transition-transform group-hover:scale-105"
          />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-gray-400 hover:text-amber-500 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="nav-testdrive-cta"
            href={waLink(`Buongiorno ${SITE.shortName}, vorrei prenotare un appuntamento o un test drive.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 whitespace-nowrap bg-amber-500 hover:bg-amber-400 text-[#0A0D14] font-semibold text-sm px-5 py-2.5 rounded-full transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.35)]"
          >
            <CalendarClock className="w-4 h-4" />
            Prenota Test Drive
          </a>
          <Link
            data-testid="nav-admin-link"
            to="/admin"
            className="p-2.5 rounded-full border border-border text-gray-500 hover:text-amber-500 hover:border-amber-500/40 transition-colors"
            aria-label="Area riservata"
          >
            <Lock className="w-4 h-4" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-gray-300"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass border-t border-border px-6 py-6 flex flex-col gap-5"
          data-testid="nav-mobile-menu"
        >
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-mobile-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="text-left font-display text-2xl font-bold uppercase tracking-wide text-gray-200 hover:text-amber-500 transition-colors"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-mobile-testdrive-cta"
            href={waLink(`Buongiorno ${SITE.shortName}, vorrei prenotare un appuntamento o un test drive.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-amber-500 text-[#0A0D14] font-semibold px-5 py-3 rounded-full"
          >
            <CalendarClock className="w-4 h-4" />
            Prenota Test Drive
          </a>
          <Link data-testid="nav-mobile-admin-link" to="/admin" className="text-gray-500 text-sm flex items-center gap-2">
            <Lock className="w-4 h-4" /> Area Riservata
          </Link>
        </motion.div>
      )}
    </motion.header>
  );
};
